/**
 * comboConfig — the declarative registry of hidden key combos.
 *
 * Same declarative spirit as effectConfig: the detector reads this
 * array, matches it against the key stream, and writes `combo.<name>` to the
 * paramBus. The shader reads those params. Adding a new combo means appending
 * one entry here — the detector and wiring don't change.
 *
 * Two kinds (see comboDetector):
 *   • sequence — keys typed in order within a short window (Konami-style). Fires
 *     a one-off: `combo.<name>` pulses to 1, then auto-releases to 0.
 *   • chord    — keys all held down at once. `combo.<name>` is 1 while the full
 *     set is held, 0 the moment any of them lifts.
 *
 * Kept deliberately hidden from the main legend; a light teaser lives in the
 * dock so they're discoverable without spoiling the reveal.
 */

export type ComboKind = 'sequence' | 'chord';

export interface ComboDef {
  /** Stable id; also the paramBus param once namespaced (`combo.<name>`). */
  readonly name: string;
  readonly kind: ComboKind;
  /** Ordered for `sequence`, an unordered set for `chord`. Normalized keys. */
  readonly keys: readonly string[];
  /** Short human label shown when the combo fires. */
  readonly label: string;
  /** One-line teaser for the dock. */
  readonly hint: string;
}

export const comboConfig: readonly ComboDef[] = [
  {
    name: 'glyph',
    kind: 'sequence',
    keys: ['G', 'L', 'Y', 'P', 'H'],
    label: 'GLYPH · supernova',
    hint: 'spell GLYPH',
  },
  {
    name: 'meltdown',
    kind: 'chord',
    keys: ['M', 'E', 'L', 'T'],
    label: 'MELTDOWN',
    hint: 'hold M+E+L+T',
  },
] as const;

/** paramBus key for a combo's active/one-off value. */
export function comboParam(name: string): string {
  return `combo.${name}`;
}
